import React, { useEffect, useState } from "react";
import { Trophy, Sparkles } from "lucide-react";
import api from "@/lib/api";

const aud = (n) => `$${(n || 0).toFixed(2)}`;
const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-2xl bg-[#FFF9EE] px-3 py-2.5 min-w-0" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider font-bold text-[#19324A]/50 truncate">{label}</p>
    <p className="font-display font-extrabold text-lg text-[#19324A] leading-tight">{value}</p>
    {sub && <p className="text-[11px] text-[#19324A]/55">{sub}</p>}
  </div>
);

export const SeasonStatsCard = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/seasons/stats").then(({ data }) => setRows(data.seasons)).catch(() => setRows([])); }, []);
  if (!rows) return null;
  const past = rows.filter((r) => !r.upcoming && (r.week_orders || r.early_visits || r.early_signups || r.early_emails_sent)), next = rows.filter((r) => r.upcoming).slice(0, 2);
  return (
    <div className="bg-white rounded-3xl soft-shadow p-6 mb-4" data-testid="season-stats-card">
      <div className="flex items-center gap-2 mb-1"><Trophy className="w-5 h-5 text-[#FFD93D]" /><h3 className="font-display font-bold text-[#19324A]">Launch day scoreboard</h3></div>
      <p className="text-xs text-[#19324A]/60 mb-4">How each occasion opened: Pack members who got in early, and what launch day brought in. Orders count paid orders containing that occasion's products.</p>
      <div className="space-y-3">
        {[...past, ...next].map((r) => (
          <div key={`${r.slug}-${r.year}`} className={`rounded-2xl border-2 p-4 ${r.upcoming ? "border-dashed border-[#19324A]/15" : "border-[#19324A]/8"}`} data-testid={`season-stats-${r.slug}-${r.year}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <p className="font-display font-bold text-[#19324A]">{r.emoji} {r.label} <span className="text-[#19324A]/50 font-normal text-sm">· {r.upcoming ? "opens" : "opened"} {fmt(r.opens)}</span></p>
              {r.upcoming
                ? <span className="text-[11px] font-bold text-[#A78BFA] bg-[#A78BFA]/10 rounded-full px-2 py-1 inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> opens in {r.days_until} day{r.days_until === 1 ? "" : "s"}</span>
                : <span className="text-[11px] font-bold text-[#4CC9F0] bg-[#4CC9F0]/10 rounded-full px-2 py-1">first week: {r.week_orders} order{r.week_orders === 1 ? "" : "s"} · {aud(r.week_revenue)}</span>}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              <Stat label="Early signups" value={r.early_signups} sub={r.waiting_list ? `${r.waiting_list} on launch list` : null} testid={`ss-signups-${r.slug}`} />
              <Stat label="Early emails" value={r.early_emails_sent} sub={`${r.launch_emails_sent} launch emails`} testid={`ss-emails-${r.slug}`} />
              <Stat label="Early visitors" value={r.early_unlocks} sub={`${r.early_visits} early visits`} testid={`ss-visitors-${r.slug}`} />
              <Stat label="Early orders" value={r.early_orders} sub={aud(r.early_revenue)} testid={`ss-early-orders-${r.slug}`} />
              <Stat label="Launch-day orders" value={r.launch_orders} sub={aud(r.launch_revenue)} testid={`ss-launch-orders-${r.slug}`} />
              <Stat label="Early share" value={r.week_orders ? `${Math.round((r.early_orders / r.week_orders) * 100)}%` : "—"} sub="of first-week orders" testid={`ss-share-${r.slug}`} />
            </div>
          </div>
        ))}
        {past.length === 0 && <p className="text-xs text-[#19324A]/50" data-testid="season-stats-empty-past">Past openings appear here once an occasion has had early-access visitors or launch orders.</p>}
      </div>
    </div>
  );
};
